import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, FileDown, PackageCheck, Trash2, ClipboardList, Lock, ChevronDown, BellRing, CalendarClock, RotateCcw } from 'lucide-react';
import ReceivePurchaseOrderModal from './ReceivePurchaseOrderModal';
import RescheduleModal from './RescheduleModal';
import DraftPurchaseOrderRow from './DraftPurchaseOrderRow';
import { toast } from 'sonner';
import { suppliersAPI } from '../services/api';

const STATUS_STYLE = { 'envoyé': 'bg-blue-100 text-blue-700', 'généré': 'bg-gray-100 text-gray-700', 'partiellement reçu': 'bg-amber-100 text-amber-700', 'reçu': 'bg-green-100 text-green-700' };
const OPEN = ['envoyé', 'généré', 'partiellement reçu'];
const remainingOf = (po) => po.lines.reduce((n, l) => n + Math.max(l.quantity - (l.received || 0), 0), 0);
const daysLate = (po) => {
  if (!po.expected_date || !OPEN.includes(po.status)) return 0;
  const exp = new Date(po.expected_date); exp.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(Math.round((today - exp) / 86400000), 0);
};

// Admin: list of purchase orders for a supplier (download, mark received, delete)
const PurchaseOrderHistory = ({ supplierId, refreshKey, onReceived }) => {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => suppliersAPI.purchaseOrders(supplierId).then(setOrders).catch(() => setOrders([])), [supplierId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remind = async (po) => {
    setBusy(po.id);
    try {
      const r = await suppliersAPI.remindPurchaseOrder(po.id);
      toast.success(r.message);
      await load();
      onReceived && onReceived();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    } finally {
      setBusy(null);
    }
  };

  const closeShort = async (po) => {
    const rest = remainingOf(po);
    if (!window.confirm(`Clôturer le reliquat de ${po.number} ? ${rest} unité(s) ne seront plus attendues.`)) return;
    setBusy(po.id);
    try {
      await suppliersAPI.closePurchaseOrder(po.id);
      toast.success('Reliquat clôturé');
      await load();
      onReceived && onReceived();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clôture impossible');
    } finally {
      setBusy(null);
    }
  };

  const reorder = async (po) => {
    const units = po.lines.reduce((n, l) => n + l.quantity, 0);
    if (!window.confirm(`Recommander à l'identique le bon ${po.number} (${po.lines.length} article(s), ${units} unités) auprès de ${po.supplier_name} ?`)) return;
    setBusy(po.id);
    try {
      const r = await suppliersAPI.reorderPurchaseOrder(po.id);
      toast.success(`Bon ${r.number} ${r.status === 'envoyé' ? 'envoyé au fournisseur' : 'généré'}`);
      await load();
      onReceived && onReceived();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Réassort impossible');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (po) => {
    if (!window.confirm(`Supprimer le bon ${po.number} ?`)) return;
    try {
      await suppliersAPI.deletePurchaseOrder(po.id);
      toast.success('Bon de commande supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  if (orders === null) return <div className="px-5 py-3"><Loader2 className="animate-spin text-orange-500" size={16} /></div>;
  if (!orders.length) return <p className="px-5 py-3 text-xs text-gray-500" data-testid="po-history-empty">Aucun bon de commande généré pour ce fournisseur.</p>;
  const drafts = orders.filter(po => po.status === 'brouillon');
  const committed = orders.filter(po => po.status !== 'brouillon');
  const changed = async () => { await load(); onReceived && onReceived(); };

  return (
    <>
    {drafts.map(po => <DraftPurchaseOrderRow key={po.id} po={po} onChanged={changed} />)}
    {committed.length > 0 && (
    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="po-history-table">
      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
        <tr>
          <th className="px-4 py-2 text-left">N° bon</th>
          <th className="px-4 py-2 text-left">Date</th>
          <th className="px-4 py-2 text-left">Livraison prévue</th>
          <th className="px-4 py-2 text-right">Articles</th>
          <th className="px-4 py-2 text-right">Unités</th>
          <th className="px-4 py-2 text-right">Reste attendu</th>
          <th className="px-4 py-2 text-right">Total HT</th>
          <th className="px-4 py-2 text-left">Statut</th>
          <th className="px-4 py-2" />
        </tr>
      </thead>
      <tbody className="divide-y">
        {committed.map(po => (
          <React.Fragment key={po.id}>
          <tr data-testid={`po-row-${po.id}`}>
            <td className="px-4 py-2 font-medium text-gray-900"><button type="button" data-testid={`po-toggle-${po.id}`} onClick={() => setOpen(open === po.id ? null : po.id)} className="flex items-center gap-1 hover:text-orange-600"><ClipboardList size={14} className="text-gray-400" /> {po.number} <ChevronDown size={12} className={`transition-transform ${open === po.id ? 'rotate-180' : ''}`} /></button></td>
            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{new Date(po.created_at).toLocaleDateString('fr-FR')}</td>
            <td className="px-4 py-2 whitespace-nowrap" data-testid={`po-expected-${po.id}`}>
              {po.expected_date ? <span className="text-gray-600">{new Date(po.expected_date).toLocaleDateString('fr-FR')}</span> : <span className="text-gray-400">—</span>}
              {daysLate(po) > 0 && <span className="ml-2 inline-block bg-red-100 text-red-700 font-semibold rounded-full px-2 py-0.5 text-[11px]" data-testid={`po-late-badge-${po.id}`}>En retard (+{daysLate(po)} j)</span>}
            </td>
            <td className="px-4 py-2 text-right">{po.lines.length}</td>
            <td className="px-4 py-2 text-right">{po.lines.reduce((n, l) => n + l.quantity, 0)}</td>
            <td className="px-4 py-2 text-right" data-testid={`po-remaining-${po.id}`}>{po.status === 'reçu' ? <span className="text-gray-400">{po.closed_short ? `clôturé (−${remainingOf(po)})` : '0'}</span> : <span className={remainingOf(po) ? 'font-semibold text-amber-700' : ''}>{remainingOf(po)}</span>}</td>
            <td className="px-4 py-2 text-right font-medium">{Number(po.total_ht).toFixed(2)} €</td>
            <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[po.status] || 'bg-gray-100 text-gray-700'}`} data-testid={`po-status-${po.id}`}>{po.status}{po.received_at ? ` le ${new Date(po.received_at).toLocaleDateString('fr-FR')}` : ''}</span></td>
            <td className="px-4 py-2">
              <div className="flex items-center justify-end gap-1">
                <button type="button" data-testid={`po-download-${po.id}`} onClick={() => suppliersAPI.downloadPurchaseOrder(po)} title="Télécharger le PDF" className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"><FileDown size={15} /></button>
                {po.status === 'reçu' && <button type="button" data-testid={`po-reorder-${po.id}`} onClick={() => reorder(po)} disabled={busy === po.id} title="Recommander les mêmes articles et quantités" className="inline-flex items-center gap-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 rounded px-2 py-1 disabled:opacity-50">{busy === po.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Recommander comme la dernière fois</button>}
                {po.status !== 'reçu' && (
                  <>
                    {daysLate(po) > 0 && <button type="button" data-testid={`po-reschedule-${po.id}`} onClick={() => setRescheduling({ ...po, days_late: daysLate(po) })} title="Nouvelle date promise par le fournisseur" className="inline-flex items-center gap-1 text-xs font-medium text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded px-2 py-1"><CalendarClock size={13} /> Nouvelle date</button>}
                    {daysLate(po) > 0 && <button type="button" data-testid={`po-remind-${po.id}`} onClick={() => remind(po)} disabled={busy === po.id} title="Relancer le fournisseur par e-mail" className="inline-flex items-center gap-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded px-2 py-1 disabled:opacity-50"><BellRing size={13} /> Relancer{(po.reminders || []).length ? ` (${po.reminders.length})` : ''}</button>}
                    <button type="button" data-testid={`po-receive-${po.id}`} onClick={() => setReceiving(po)} disabled={busy === po.id} title="Réceptionner (total ou partiel)" className="inline-flex items-center gap-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded px-2 py-1 disabled:opacity-50"><PackageCheck size={13} /> Réceptionner</button>
                    {(po.receipts || []).length > 0 && <button type="button" data-testid={`po-close-${po.id}`} onClick={() => closeShort(po)} disabled={busy === po.id} title="Clôturer le reliquat" className="p-1.5 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"><Lock size={15} /></button>}
                    {!(po.receipts || []).length && <button type="button" data-testid={`po-delete-${po.id}`} onClick={() => remove(po)} title="Supprimer" className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={15} /></button>}
                  </>
                )}
              </div>
            </td>
          </tr>
          {open === po.id && (
            <tr data-testid={`po-detail-${po.id}`}><td colSpan={9} className="px-6 py-3 bg-gray-50/70">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="font-semibold text-gray-600 uppercase mb-1">Lignes</p>
                  <ul className="space-y-0.5">{po.lines.map(l => <li key={l.product_id} className="flex justify-between gap-2"><span className="truncate">{l.name}</span><span className="whitespace-nowrap text-gray-600">{l.received || 0} / {l.quantity} reçu{(l.received || 0) > l.quantity ? <span className="text-amber-700"> (surplus +{(l.received || 0) - l.quantity})</span> : null}</span></li>)}</ul>
                </div>
                <div>
                  <p className="font-semibold text-gray-600 uppercase mb-1">Réceptions</p>
                  {(po.receipts || []).length === 0 ? <p className="text-gray-400">Aucune réception enregistrée.</p> : (
                    <ul className="space-y-0.5" data-testid={`po-receipts-${po.id}`}>{po.receipts.map(r => <li key={r.id} className="flex justify-between gap-2"><span>{new Date(r.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {r.author}{r.note ? ` · ${r.note}` : ''}</span><span className="font-medium text-green-700 whitespace-nowrap">+{r.units} u.</span></li>)}</ul>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-600 uppercase mb-1">Relances fournisseur</p>
                  {(po.reminders || []).length === 0 ? <p className="text-gray-400">Aucune relance envoyée.</p> : (
                    <ul className="space-y-0.5" data-testid={`po-reminders-${po.id}`}>{po.reminders.map(r => <li key={r.id} className="flex justify-between gap-2"><span>{new Date(r.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {r.author}</span><span className="font-medium text-red-700 whitespace-nowrap">+{r.days_late} j de retard</span></li>)}</ul>
                  )}
                  {(po.reschedules || []).length > 0 && (
                    <>
                      <p className="font-semibold text-gray-600 uppercase mb-1 mt-3">Reports de livraison</p>
                      <ul className="space-y-0.5" data-testid={`po-reschedules-${po.id}`}>{po.reschedules.map(r => <li key={r.id} className="flex justify-between gap-2"><span>{new Date(r.date).toLocaleDateString('fr-FR')} · {r.old ? new Date(r.old).toLocaleDateString('fr-FR') : '—'} → <strong>{new Date(r.new).toLocaleDateString('fr-FR')}</strong>{r.note ? ` · ${r.note}` : ''}</span></li>)}</ul>
                    </>
                  )}
                </div>
              </div>
            </td></tr>
          )}
          </React.Fragment>
        ))}
      </tbody>
    </table></div>
    )}
    {receiving && <ReceivePurchaseOrderModal po={receiving} onClose={() => setReceiving(null)} onReceived={async () => { setReceiving(null); await load(); onReceived && onReceived(); }} />}
    {rescheduling && <RescheduleModal po={rescheduling} onClose={() => setRescheduling(null)} onSaved={async () => { setRescheduling(null); await load(); onReceived && onReceived(); }} />}
    </>
  );
};

export default PurchaseOrderHistory;
